// src/pages/Billing.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { IndianRupee, ChevronDown, ChevronUp, Tag, CreditCard, Clock } from 'lucide-react';
import { Modal } from './Tables';

export default function Billing() {
  const [pending,  setPending]  = useState([]);
  const [detail,   setDetail]   = useState(null);   // customer detail modal
  const [payModal, setPayModal] = useState(null);   // {customer} being paid
  const [payForm,  setPayForm]  = useState({ discount_type:'none', discount_value:'', payment_mode:'cash' });
  const [expanded, setExpanded] = useState({});     // expanded session rows
  const [loading,  setLoading]  = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/billing?action=pending');
      setPending(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load billing data'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (customer) => {
    try {
      const res = await api.get(`/billing?action=customer_detail&customer_id=${customer.customer_id}`);
      setDetail(res.data);
    } catch { toast.error('Failed to load customer detail'); }
  };

  const openPay = (customer) => {
    setPayModal(customer);
    setPayForm({ discount_type:'none', discount_value:'', payment_mode:'cash' });
  };

  const calcDiscount = () => {
    if (!payModal) return 0;
    const base = parseFloat(payModal.total_amount);
    if (payForm.discount_type === 'percent' && payForm.discount_value) {
      return Math.round(base * (parseFloat(payForm.discount_value)/100) * 100)/100;
    }
    if (payForm.discount_type === 'amount' && payForm.discount_value) {
      return Math.min(parseFloat(payForm.discount_value), base);
    }
    return 0;
  };

  const finalAmount = () => {
    if (!payModal) return 0;
    return Math.max(0, parseFloat(payModal.total_amount) - calcDiscount());
  };

  const submitPayment = async () => {
    setLoading(true);
    try {
      await api.post('/billing?action=pay', {
        customer_id:    payModal.customer_id,
        discount_type:  payForm.discount_type,
        discount_value: parseFloat(payForm.discount_value) || 0,
        payment_mode:   payForm.payment_mode,
      });
      toast.success(`Payment collected — ₹${finalAmount().toFixed(2)} from ${payModal.customer_name}`);
      setPayModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const fmt = n => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });
  const fmtSecs = s => { const h=Math.floor(s/3600),m=Math.floor((s%3600)/60); return `${h}h ${m}m`; };
  const totalPending = pending.reduce((a,c) => a + parseFloat(c.total_amount), 0);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Billing</h2>
        {pending.length > 0 && (
          <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:8, padding:'6px 14px', fontSize:13, color:'#92400e' }}>
            <strong>{pending.length}</strong> customer{pending.length!==1?'s':''} pending — Total: <strong>{fmt(totalPending)}</strong>
          </div>
        )}
      </div>

      {pending.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:60 }}>
          <IndianRupee size={48} color="#94a3b8" style={{ margin:'0 auto 12px' }}/>
          <h3 style={{ color:'#64748b', marginBottom:8 }}>No Pending Payments</h3>
          <p style={{ color:'#94a3b8', fontSize:14 }}>All customers are settled. End a session to add charges here.</p>
        </div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Sessions</th>
                <th>Tables Played</th>
                <th style={{ textAlign:'right' }}>Play</th>
                <th style={{ textAlign:'right' }}>Food</th>
                <th style={{ textAlign:'right' }}>Total Due</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map(c => (
                <tr key={c.customer_id} style={{ cursor:'default' }}>
                  <td>
                    <div style={{ fontWeight:700, fontSize:14 }}>{c.customer_name}</div>
                    <div style={{ fontSize:12, color:'#94a3b8' }}>{c.mobile}</div>
                  </td>
                  <td>
                    <span style={{ background:'#eff6ff', color:'#2563eb', padding:'2px 10px', borderRadius:12, fontSize:12, fontWeight:600 }}>
                      {c.session_count} session{c.session_count!==1?'s':''}
                    </span>
                  </td>
                  <td style={{ fontSize:12, color:'#64748b' }}>{c.tables_played}</td>
                  <td style={{ textAlign:'right' }}>{fmt(c.play_amount)}</td>
                  <td style={{ textAlign:'right' }}>{fmt(c.food_amount)}</td>
                  <td style={{ textAlign:'right', fontWeight:700, fontSize:15, color:'#1e3a5f' }}>{fmt(c.total_amount)}</td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openDetail(c)}>
                        Details
                      </button>
                      <button className="btn btn-success btn-sm" onClick={() => openPay(c)}>
                        <CreditCard size={13}/> Collect
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background:'#fef9c3', fontWeight:700 }}>
                <td colSpan={3}>Total Pending</td>
                <td style={{ textAlign:'right' }}>{fmt(pending.reduce((a,c)=>a+parseFloat(c.play_amount),0))}</td>
                <td style={{ textAlign:'right' }}>{fmt(pending.reduce((a,c)=>a+parseFloat(c.food_amount),0))}</td>
                <td style={{ textAlign:'right', color:'#1e3a5f', fontSize:15 }}>{fmt(totalPending)}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Detail Modal — session breakdown */}
      {detail && (
        <Modal title={`${detail.customer.name} — Session Details`} onClose={() => setDetail(null)}>
          <div style={{ marginBottom:12, display:'flex', gap:8 }}>
            <span style={{ background:'#f1f5f9', padding:'4px 12px', borderRadius:12, fontSize:13 }}>📱 {detail.customer.mobile}</span>
            <span style={{ background:'#fef3c7', color:'#92400e', padding:'4px 12px', borderRadius:12, fontSize:13 }}>{detail.sessions.length} pending session{detail.sessions.length!==1?'s':''}</span>
          </div>

          <div style={{ maxHeight:420, overflowY:'auto' }}>
            {detail.sessions.map((sess, idx) => (
              <div key={sess.session_id} style={{ border:'1px solid #e2e8f0', borderRadius:10, marginBottom:10, overflow:'hidden' }}>
                {/* Session header */}
                <div
                  style={{ background:'#f8fafc', padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}
                  onClick={() => setExpanded(ex => ({...ex, [sess.session_id]: !ex[sess.session_id]}))}
                >
                  <div>
                    <span style={{ fontWeight:600 }}>{sess.table_name}</span>
                    <span style={{ fontSize:12, color:'#94a3b8', marginLeft:10 }}>
                      {new Date(sess.session_start).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                    </span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontWeight:700, color:'#1e3a5f' }}>{fmt(sess.play_amount + sess.food_amount)}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>
                        Play: {fmt(sess.play_amount)} + Food: {fmt(sess.food_amount)}
                      </div>
                    </div>
                    {expanded[sess.session_id] ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded[sess.session_id] && (
                  <div style={{ padding:'10px 14px' }}>
                    <div style={{ display:'flex', gap:16, fontSize:12, color:'#64748b', marginBottom:8 }}>
                      <span><Clock size={12}/> Play time: {fmtSecs(sess.total_play_seconds)}</span>
                    </div>
                    {sess.food_orders.length > 0 && (
                      <>
                        <div style={{ fontSize:12, fontWeight:600, color:'#334155', marginBottom:6 }}>Food Orders:</div>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                          <thead><tr style={{ background:'#f1f5f9' }}>
                            <th style={{ padding:'4px 8px', textAlign:'left' }}>Item</th>
                            <th style={{ padding:'4px 8px', textAlign:'center' }}>Qty</th>
                            <th style={{ padding:'4px 8px', textAlign:'right' }}>Total</th>
                          </tr></thead>
                          <tbody>
                            {sess.food_orders.map(fo => (
                              <tr key={fo.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'4px 8px' }}>{fo.item_name}</td>
                                <td style={{ padding:'4px 8px', textAlign:'center' }}>{fo.quantity}</td>
                                <td style={{ padding:'4px 8px', textAlign:'right' }}>{fmt(fo.total_price)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {sess.food_orders.length === 0 && <div style={{ fontSize:12, color:'#94a3b8' }}>No food orders this session</div>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Total across all sessions */}
          <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'12px 16px', marginTop:4 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:4 }}>
              <span>Total Play</span><span>{fmt(detail.sessions.reduce((a,s)=>a+s.play_amount,0))}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:8 }}>
              <span>Total Food</span><span>{fmt(detail.sessions.reduce((a,s)=>a+s.food_amount,0))}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:16, fontWeight:700, color:'#1e3a5f' }}>
              <span>Grand Total Due</span>
              <span>{fmt(detail.sessions.reduce((a,s)=>a+s.play_amount+s.food_amount,0))}</span>
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop:16 }}>
            <button className="btn btn-outline" onClick={() => setDetail(null)}>Close</button>
            <button className="btn btn-success" onClick={() => {
              const c = pending.find(p => p.customer_id === detail.customer.id);
              if (c) { setDetail(null); openPay(c); }
            }}>
              <CreditCard size={14}/> Collect Payment
            </button>
          </div>
        </Modal>
      )}

      {/* Payment Modal */}
      {payModal && (
        <Modal title={`Collect Payment — ${payModal.customer_name}`} onClose={() => setPayModal(null)}>
          {/* Amount breakdown */}
          <div className="bill-time-summary" style={{ marginBottom:16 }}>
            <div className="bill-time-row">
              <span>Sessions</span><span>{payModal.session_count}</span>
            </div>
            <div className="bill-time-row">
              <span>Tables</span><span style={{ fontSize:12 }}>{payModal.tables_played}</span>
            </div>
            <div className="bill-time-row">
              <span>Play Charges</span><span>{fmt(payModal.play_amount)}</span>
            </div>
            <div className="bill-time-row">
              <span>Food Charges</span><span>{fmt(payModal.food_amount)}</span>
            </div>
            <div className="bill-time-row total">
              <span>Subtotal</span><span>{fmt(payModal.total_amount)}</span>
            </div>
          </div>

          {/* Discount */}
          <div className="form-group">
            <label><Tag size={14} style={{ marginRight:4 }}/>Discount (optional)</label>
            <div style={{ display:'flex', gap:8 }}>
              <select style={{ flex:1 }} value={payForm.discount_type}
                onChange={e => setPayForm(f => ({...f, discount_type:e.target.value, discount_value:''}))}>
                <option value="none">No Discount</option>
                <option value="percent">% Percentage</option>
                <option value="amount">₹ Flat Amount</option>
              </select>
              {payForm.discount_type !== 'none' && (
                <input type="number" min="0" step="0.01" style={{ flex:1 }}
                  value={payForm.discount_value}
                  onChange={e => setPayForm(f => ({...f, discount_value:e.target.value}))}
                  placeholder={payForm.discount_type==='percent'?'e.g. 10':'e.g. 50'}
                />
              )}
            </div>
            {calcDiscount() > 0 && (
              <div style={{ marginTop:6, fontSize:13, color:'#22c55e', fontWeight:600 }}>
                Discount: -{fmt(calcDiscount())}
              </div>
            )}
          </div>

          {/* Final amount */}
          <div style={{ background: calcDiscount()>0?'#f0fdf4':'#eff6ff', border:'1px solid', borderColor:calcDiscount()>0?'#bbf7d0':'#bfdbfe', borderRadius:10, padding:'14px 18px', margin:'8px 0 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:15, fontWeight:600, color:'#334155' }}>Final Amount</span>
            <span style={{ fontSize:24, fontWeight:800, color:'#1e3a5f' }}>{fmt(finalAmount())}</span>
          </div>

          {/* Payment mode */}
          <div className="form-group">
            <label>Payment Mode</label>
            <div style={{ display:'flex', gap:8 }}>
              {['cash','upi','other'].map(m => (
                <button key={m}
                  className={`btn btn-sm ${payForm.payment_mode===m?'btn-primary':'btn-outline'}`}
                  style={{ flex:1, textTransform:'capitalize' }}
                  onClick={() => setPayForm(f => ({...f, payment_mode:m}))}>
                  {m === 'cash' ? '💵' : m === 'upi' ? '📱' : '💳'} {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setPayModal(null)}>Cancel</button>
            <button className="btn btn-success" onClick={submitPayment} disabled={loading}>
              <IndianRupee size={14}/> {loading ? 'Processing…' : `Confirm ₹${finalAmount().toFixed(2)}`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
